import Rest from "Utils/Rest";
import UserContactInformationModel from "Models/User/UserContactInformationModel";
import RegisterDirectMessagingModel from "Models/User/RegisterDirectMessagingModel";

import ServiceResponseWithData = NetHealth.EHR.Domain.Models.ServiceResponseWithData;
import { UserPanelConstants } from "Constants";
import UserEMModel = NetHealth.EHR.Domain.Models.UserEMModel;
import ClientSession from "ClientSession";

export default class UserService {
    getContactInformation() {
        return Rest.GET<UserContactInformationModel>("User/ContactInformation", {});
    }

    saveContactInformation(data: UserContactInformationModel) {
        return Rest.PUT("User/ContactInformation", data);
    }

    getAutomatePhysicianEm() {
        return Rest.GET<UserEMModel>("User/AutomatePhysicianEM", {});
    }

    setAutomatePhysicianEm(data: UserEMModel) {
        return Rest.PUT("User/AutomatePhysicianEM", data);
    }

    getDefaultPhysicianOrdersTab() {
        return Rest.GET<string>("User/DefaultPhysicianOrdersTab", {});
    }

    setDefaultPhysicianOrdersTab(data: string) {
        return Rest.PUT("User/DefaultPhysicianOrdersTab", data);
    }

    getLogPatientCommunications() {
        return Rest.GET<boolean>("User/LogPatientCommunications", {});
    }

    setLogPatientCommunications(data: boolean) {
        return Rest.PUT("User/LogPatientCommunications", data);
    }

    getHomepage() {
        return Rest.GET<NetHealth.EHR.Domain.Models.User.HomepageEditModel>("User/Homepage", {});
    }

    setHomepage(pageId: number) {
        return Rest.PUT("User/Homepage", { selectedPageId: pageId });
    }

    getDefaultSearchCriteria() {
        return Rest.GET<string>("User/SearchCriteria", {});
    }

    getHasRequiredSignatures() {
        return Rest.GET<boolean>("User/GetHasRequiredSignatures", {});
    }

    setDefaultSearchCriteria(data: string) {
        return Rest.PUT("User/SearchCriteria", data);
    }

    removeProfileImage() {
        return Rest.DELETE("User/ProfileImage", {});
    }

    updateProfileImage(fileExtension: string) {
        return Rest.POST("User/UpdateImageExtension", fileExtension);
    }

    getUsersList() {
        return Rest.GET<ServiceResponseWithData<NetHealth.EHR.Domain.Models.User.UserModel[]>>("User/List", {});
    }

    getUsersListForFacility() {
        return Rest.GET<ServiceResponseWithData<NetHealth.EHR.Domain.Models.User.UserModel[]>>("User/FacilityList", {});
    }

    registerDirectMessaging(data: RegisterDirectMessagingModel) {
        return Rest.POST("User/RegisterDirectMessaging", data);
    }

    checkDirectMessageUserExists(data: RegisterDirectMessagingModel) {
        return Rest.POST("User/CheckDirectMessageUserExists", data);
    }

    checkDirectMessagingEnabled() {
        return Rest.GET<boolean>("User/CheckDirectMessagingEnabled", {});
    }

    blockRequiredSignaturesMessage = () => {
        return Rest.POST<any>("User/BlockRequiredSignaturesMessage", {});
    }

    blockSystemMessage = (blockMessageIDs: string) => {
        return Rest.PUT<any>("User/BlockSystemMessage", blockMessageIDs);
    }

    getStates() {
        return Rest.GET("User/States", {});
    }

    getXfitTutorialDisplay() {
        return Rest.GET<boolean>("User/XfitTutorialDisplayGet", {});
    }

    setXfitTutorialDisplay() {
        return Rest.PUT("User/XfitTutorialDisplaySet", {});
    }

    //CS 1-21-19: These "Collapsed" variables have to return reactive values.
    isActivityFeedCollapsed() {
        return ClientSession.user.isActivityFeedCollapsed;
    }

    isPatientPanelCollapsed() {
        return ClientSession.user.isPatientPanelCollapsed;
    }

    isProgressNoteCollapsed() {
        return ClientSession.user.isProgressNoteCollapsed;
    }

    isSchedulerFilterPanelCollapsed() {
        return ClientSession.user.isSchedulerFilterPanelCollapsed;
    }

    isVerticalWorkflowCollapsed() {
        return ClientSession.user.isVerticalWorkflowCollapsed;
    }

    isTimelineViewEnabled() {
        return ClientSession.user.calendar.isTimelineView;
    }

    isCalendarExpandedViewEnabled() {
        return ClientSession.user.calendar.showExpandedTimeline;
    }

    schedulerShowUnavailableProviders() {
        return ClientSession.user.schedulerShowUnavailableProviders;
    }

    toggleUserPanelSetting = (panelName: string) => {
        var userPanelSettings = <NetHealth.EHR.Domain.Models.User.UserPanelSettingsModel>
            {
                panelName: panelName,
                isActivityFeedCollapsed: ClientSession.user.isActivityFeedCollapsed,
                isPatientPanelCollapsed: ClientSession.user.isPatientPanelCollapsed,
                isProgressNoteCollapsed: ClientSession.user.isProgressNoteCollapsed,
                isSchedulerFilterPanelCollapsed: ClientSession.user.isSchedulerFilterPanelCollapsed,
                isVerticalWorkflowCollapsed: ClientSession.user.isVerticalWorkflowCollapsed,
                isTimelineViewEnabled: ClientSession.user.calendar.isTimelineView,
                isCalendarExpandedViewEnabled: ClientSession.user.calendar.showExpandedTimeline,
                schedulerShowUnavailableProviders: ClientSession.user.schedulerShowUnavailableProviders
            };

        switch (panelName) {
            case UserPanelConstants.ACTIVITY_FEED:
                userPanelSettings.isActivityFeedCollapsed = !userPanelSettings.isActivityFeedCollapsed;
                break;

            case UserPanelConstants.PATIENT_PANEL:
                userPanelSettings.isPatientPanelCollapsed = !userPanelSettings.isPatientPanelCollapsed;
                break;

            case UserPanelConstants.PROGRESS_NOTE:
                userPanelSettings.isProgressNoteCollapsed = !userPanelSettings.isProgressNoteCollapsed;
                break;

            case UserPanelConstants.SCHEDULER_FILTER_PANEL:
                userPanelSettings.isSchedulerFilterPanelCollapsed = !userPanelSettings.isSchedulerFilterPanelCollapsed;
                break;

            case UserPanelConstants.VERTICAL_WORKFLOW:
                userPanelSettings.isVerticalWorkflowCollapsed = !userPanelSettings.isVerticalWorkflowCollapsed;
                break;

            case UserPanelConstants.CALENDAR_TIMELINE_VIEW:
                userPanelSettings.isTimelineViewEnabled = !userPanelSettings.isTimelineViewEnabled;  
                if (userPanelSettings.isTimelineViewEnabled !== true)
                    userPanelSettings.isCalendarExpandedViewEnabled = false;                
                break;
            case UserPanelConstants.CALENDAR_EXPANDED_VIEW:
                userPanelSettings.isCalendarExpandedViewEnabled = !userPanelSettings.isCalendarExpandedViewEnabled;
                break;
            case UserPanelConstants.SCHEDULER_SHOW_UNAVAILABLE_PROVIDERS:
                userPanelSettings.schedulerShowUnavailableProviders = !userPanelSettings.schedulerShowUnavailableProviders;
                break;

            default:
                return Promise.resolve(userPanelSettings);
        }

        return this.updateUserPanelSettings(userPanelSettings.panelName,
            userPanelSettings.isPatientPanelCollapsed,
            userPanelSettings.isActivityFeedCollapsed,
            userPanelSettings.isVerticalWorkflowCollapsed,
            userPanelSettings.isProgressNoteCollapsed,
            userPanelSettings.isSchedulerFilterPanelCollapsed,
            userPanelSettings.isTimelineViewEnabled,
            userPanelSettings.isCalendarExpandedViewEnabled,
            userPanelSettings.schedulerShowUnavailableProviders).then((panelInfo: NetHealth.EHR.Domain.Models.User.UserPanelSettingsModel) => {

                ClientSession.user.isActivityFeedCollapsed = panelInfo.isActivityFeedCollapsed;
                ClientSession.user.isPatientPanelCollapsed = panelInfo.isPatientPanelCollapsed;
                ClientSession.user.isProgressNoteCollapsed = panelInfo.isProgressNoteCollapsed;
                ClientSession.user.isSchedulerFilterPanelCollapsed = panelInfo.isSchedulerFilterPanelCollapsed;
                ClientSession.user.isVerticalWorkflowCollapsed = panelInfo.isVerticalWorkflowCollapsed;
                ClientSession.user.calendar.isTimelineView = panelInfo.isTimelineViewEnabled;
                ClientSession.user.calendar.showExpandedTimeline = panelInfo.isCalendarExpandedViewEnabled;
                ClientSession.user.schedulerShowUnavailableProviders = panelInfo.schedulerShowUnavailableProviders;

                return panelInfo;
            }
            , (panelInfo: NetHealth.EHR.Domain.Models.User.UserPanelSettingsModel) => {

                // Fall back to updating the UI without saving state
                ClientSession.user.isActivityFeedCollapsed = userPanelSettings.isActivityFeedCollapsed;

                // If vertical workflow  is expanded, patient panel should be collapsed
                if (panelName === UserPanelConstants.VERTICAL_WORKFLOW && !userPanelSettings.isVerticalWorkflowCollapsed)
                    ClientSession.user.isPatientPanelCollapsed = true;
                else
                    ClientSession.user.isPatientPanelCollapsed = userPanelSettings.isPatientPanelCollapsed;

                ClientSession.user.isProgressNoteCollapsed = userPanelSettings.isProgressNoteCollapsed;
                ClientSession.user.isSchedulerFilterPanelCollapsed = userPanelSettings.isSchedulerFilterPanelCollapsed;

                // If patient panel is expanded, vertical workflow should be collapsed
                if (panelName === UserPanelConstants.PATIENT_PANEL && !userPanelSettings.isPatientPanelCollapsed)
                    ClientSession.user.isVerticalWorkflowCollapsed = true;
                else
                    ClientSession.user.isVerticalWorkflowCollapsed = userPanelSettings.isVerticalWorkflowCollapsed;

                return userPanelSettings;
            });
    }


    updateUserPanelSettings = (panelName: string, isPatientPanelCollapsed: boolean, isActivityFeedCollapsed: boolean,
        isVerticalWorkflowCollapsed: boolean, isProgressNoteCollapsed: boolean, isSchedulerFilterPanelCollapsed: boolean,
        isTimelineViewEnabled: boolean, isCalendarExpandedViewEnabled: boolean, schedulerShowUnavailableProviders: boolean) => {        
        return Rest.POST<NetHealth.EHR.Domain.Models.User.UserPanelSettingsModel>("User/UpdateUserPanelSettings",
            {
                panelName: panelName,
                isPatientPanelCollapsed: isPatientPanelCollapsed,
                isActivityFeedCollapsed: isActivityFeedCollapsed,
                isVerticalWorkflowCollapsed: isVerticalWorkflowCollapsed,
                isProgressNoteCollapsed: isProgressNoteCollapsed,
                isSchedulerFilterPanelCollapsed: isSchedulerFilterPanelCollapsed,
                isTimelineViewEnabled: isTimelineViewEnabled,
                isCalendarExpandedViewEnabled: isCalendarExpandedViewEnabled,
                schedulerShowUnavailableProviders: schedulerShowUnavailableProviders
            }
        );
    }

    getUserID(userName: string) {
        return Rest.GET<string>("User/Id", { userName: userName });
    }

    getListofFacilitiesForUser(userId?: string, removeCurrentFacility?: boolean): Promise<NetHealth.EHR.Domain.UiModels.FacilityModel[]> {
        return Rest.GET<NetHealth.EHR.Domain.UiModels.FacilityModel[]>('User/UserFacilityList', {userId, removeCurrentFacility});
    }
}
